import { useEffect, useState } from 'react';
import styled from 'styled-components';
import MarkdownIt from 'markdown-it';
import markdownItModifyToken from 'markdown-it-modify-token';
import GameCard from '../GameCard';
import Tab from './Tab';
import Panel from './Panel';
import { getEntry, getAllEntries } from '../../services/TournamentOld';

const mdParser = new MarkdownIt({ typographer: true });
mdParser.use(markdownItModifyToken);

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  grid-column-gap: 3.28%;
  grid-row-gap: 59px;
  text-align: left;
  grid-template-areas:
    'header'
    'standings'
    'info';

  @media screen and (min-width: 1200px) {
    min-width: calc(75vw - 100px);
    grid-template-columns: 2fr 1fr;
    grid-template-areas:
      'header header'
      'standings info';
  }

  @media screen and (min-width: 1600px) {
    min-width: 1100px;
  }
`;

const Heading = styled.h2(({ theme }) => {
  const { color, type } = theme;

  return `
    color: ${color.primary.regular};
    font-size: ${type.size.larger};
    font-weight: ${type.weight.bold};
    text-align: center;
    font-family: ${type.face};
    grid-area: header;
  `;
});

const Standings = styled.div(({ theme }) => {
  const { color, shadow } = theme;

  return `
    background-color: ${color.grayscale[1000]};
    grid-area: standings;
    padding: 17px 32px;
    border-radius: 16px;
    box-shadow: ${shadow.regular};
  `;
});

const Info = styled.div`
  grid-area: info;
`;

const TabList = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
`;

const Description = styled.div`
  margin-bottom: 16px;

  h1,
  h2,
  h3,
  h4,
  h5,
  h6,
  p,
  ul,
  ol {
    margin-bottom: 1rem;
  }
`;

const TournamentDialog = ({ tournament, launchTournament }) => {
  const [activeTab, setActiveTab] = useState();
  const [standings, setStandings] = useState([]);

  useEffect(() => {
    getAllEntries(tournament.id).then((results) => {
      setStandings(results);
      setActiveTab(results[0].typeId);
    });
  }, [tournament.id]);

  const handleUpdate = (page, daily = false) => {
    const convertPage = page - 1; // Modifies human-readable page to first array index

    getEntry(tournament.id, activeTab, convertPage, daily).then((results) => {
      const newStandings = [...standings];
      newStandings[activeTab - 1] = results;
      setStandings(newStandings);
    });
  };

  const handleTabClick = (tab) => {
    getEntry(tournament.id, tab, 0, false).then((results) => {
      const newStandings = [...standings];

      newStandings[tab - 1] = results;
      setStandings(newStandings);
      setActiveTab(tab);
    });
  };

  const tabLoop = standings.map((tab) => {
    return (
      <Tab
        key={`tournamentTab-${tab.typeId}`}
        active={activeTab === tab.typeId}
        callback={() => handleTabClick(tab.typeId)}
        title={tab.title}
        id={tab.typeId}
      />
    );
  });

  const panelLoop = standings.map((panel) => {
    return (
      <Panel
        key={`tournamentPanel-${panel.typeId}`}
        panelData={panel}
        active={activeTab === panel.typeId}
        updateCallback={handleUpdate}
      />
    );
  });

  return (
    <Grid>
      <Heading>{tournament.name} Leaderboard</Heading>

      <Standings>
        <TabList role="tablist" aria-label="Tournament Standings">
          {tabLoop}
        </TabList>

        {panelLoop}
      </Standings>

      <Info>
        <Description dangerouslySetInnerHTML={{ __html: mdParser.render(tournament.info) }} />
        <GameCard
          title={tournament.game_name}
          description=""
          gameName={tournament.game_name}
          gameImage={tournament.game_image}
          isVisible
          callback={() => {
            launchTournament(tournament.game_id);
          }}
          hidePlayButton={!tournament.active}
        />
      </Info>
    </Grid>
  );
};

export default TournamentDialog;
